import AsyncStorage from '@react-native-async-storage/async-storage';
import { useCallback, useEffect, useState } from 'react';

import { useAuth } from '@/components/providers/AuthProvider';
import type { CommunityCode } from '@/constants/communities';
import dayjs from '@/i18n/dayjs-config';

const DISMISS_DURATION_MONTHS = 6;

function getStorageKey(community: CommunityCode): string {
  return `safeModeReminderDismissed.${community}`;
}

function isDismissStillActive(stored: string | null): boolean {
  if (!stored) return false;
  if (stored === 'true') return false;

  const dismissedAt = dayjs(stored);
  if (!dismissedAt.isValid()) return false;

  return dayjs().diff(dismissedAt, 'month', true) < DISMISS_DURATION_MONTHS;
}

type Listener = (isDismissed: boolean) => void;
const listenersByCommunity = new Map<CommunityCode, Set<Listener>>();

function notifyListeners(community: CommunityCode, isDismissed: boolean) {
  listenersByCommunity.get(community)?.forEach((listener) => listener(isDismissed));
}

export function useSensitivePhotosReminder() {
  const { currentCommunity } = useAuth();
  const [isDismissed, setIsDismissed] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!currentCommunity) {
      setIsDismissed(false);
      setIsLoaded(true);
      return;
    }

    let cancelled = false;
    setIsLoaded(false);

    (async () => {
      try {
        const key = getStorageKey(currentCommunity);
        const stored = await AsyncStorage.getItem(key);
        const stillDismissed = isDismissStillActive(stored);

        if (!stillDismissed && stored) {
          await AsyncStorage.removeItem(key);
        }

        if (!cancelled) {
          setIsDismissed(stillDismissed);
        }
      } catch (error) {
        console.error('Failed to load sensitive photos reminder preference', error);
      } finally {
        if (!cancelled) {
          setIsLoaded(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentCommunity]);

  useEffect(() => {
    if (!currentCommunity) return;

    const listener: Listener = (nextIsDismissed) => setIsDismissed(nextIsDismissed);
    let communityListeners = listenersByCommunity.get(currentCommunity);
    if (!communityListeners) {
      communityListeners = new Set();
      listenersByCommunity.set(currentCommunity, communityListeners);
    }
    communityListeners.add(listener);

    return () => {
      communityListeners?.delete(listener);
    };
  }, [currentCommunity]);

  const dismiss = useCallback(async () => {
    if (!currentCommunity) return;

    setIsDismissed(true);
    notifyListeners(currentCommunity, true);

    try {
      await AsyncStorage.setItem(getStorageKey(currentCommunity), dayjs().toISOString());
    } catch (error) {
      console.error('Failed to save sensitive photos reminder preference', error);
    }
  }, [currentCommunity]);

  return { isDismissed, isLoaded, dismiss };
}
